import { useCallback, useEffect, useRef, useState } from 'react';
import { X, ArrowDown, ArrowUp, Trash2, Pause, RefreshCw, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../contexts/SocketContext';
import { getResponseErrorMessage } from '../utils/apiError';

interface LogsModalProps {
  serverId: string;
  serverName: string;
  onClose: () => void;
}

interface LogEntry {
  timestamp: string;
  message: string;
  id: string;
  stream: 'stdout' | 'stderr';
}

interface WireLogEntry {
  id?: unknown;
  stream?: unknown;
  timestamp?: unknown;
  message?: unknown;
}

const MAX_VISIBLE_LOGS = 5_000;

function parseLogLine(line: string, index: number): LogEntry {
  const timestampMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z)\s+(.*)$/);
  if (timestampMatch) {
    return { timestamp: timestampMatch[1], message: timestampMatch[2], id: `legacy-${index}-${line}`, stream: 'stdout' };
  }

  const altTimestampMatch = line.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s+(.*)$/);
  if (altTimestampMatch) {
    return {
      timestamp: new Date(altTimestampMatch[1]).toISOString(),
      message: altTimestampMatch[2],
      id: `legacy-${index}-${line}`,
      stream: 'stdout',
    };
  }

  return { timestamp: new Date().toISOString(), message: line, id: `legacy-${index}-${line}`, stream: 'stdout' };
}

function parseWireEntry(value: WireLogEntry): LogEntry | null {
  if (typeof value.id !== 'string' || typeof value.message !== 'string') return null;
  return {
    id: value.id,
    message: value.message,
    stream: value.stream === 'stderr' ? 'stderr' : 'stdout',
    timestamp: typeof value.timestamp === 'string' && Number.isFinite(Date.parse(value.timestamp))
      ? value.timestamp
      : new Date().toISOString(),
  };
}

function parseWireEntries(value: unknown): LogEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: LogEntry[] = [];
  for (const candidate of value as WireLogEntry[]) {
    const entry = parseWireEntry(candidate);
    if (entry) entries.push(entry);
  }
  return entries;
}

export function LogsModal({ serverId, serverName, onClose }: LogsModalProps) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [totalLogCount, setTotalLogCount] = useState(100);
  const [error, setError] = useState('');
  const [snapshotLoaded, setSnapshotLoaded] = useState(false);
  const [liveReady, setLiveReady] = useState(false);
  const [streamAttempt, setStreamAttempt] = useState(0);
  const { socket, connected } = useSocket();
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const followCursorRef = useRef<string | undefined>(undefined);
  const seenIdsRef = useRef(new Set<string>());

  const scrollToBottom = () => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (autoScroll) scrollToBottom();
  }, [logs, autoScroll]);

  useEffect(() => {
    if (!isLoading && logs.length > 0) {
      setTimeout(() => {
        logsEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [isLoading]);

  const loadInitialLogs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSnapshotLoaded(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/logs/${serverId}?tail=100`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(await getResponseErrorMessage(response, t('dockerLogs.loadFailed')));
      const data = await response.json();
      const structured = parseWireEntries(data.entries);
      const rawLogs = typeof data.logs === 'string'
        ? data.logs
        : (Array.isArray(data.logs) ? data.logs.join('\n') : '');
      const logLines: string[] = String(rawLogs).split('\n').filter((line: string) => line.trim() !== '');
      const parsedLogs: LogEntry[] = structured.length > 0 ? structured : logLines.map(parseLogLine);
      seenIdsRef.current = new Set(parsedLogs.map(entry => entry.id));
      followCursorRef.current = typeof data.cursor === 'string' && Number.isFinite(Date.parse(data.cursor))
        ? data.cursor
        : parsedLogs.at(-1)?.timestamp;
      setLogs(parsedLogs);
      setTotalLogCount(parsedLogs.length);
      setLiveReady(false);
      setSnapshotLoaded(true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('dockerLogs.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [serverId, t]);

  useEffect(() => {
    void loadInitialLogs();
  }, [loadInitialLogs]);

  useEffect(() => {
    if (!socket || !connected || !snapshotLoaded) return;

    let retryTimer: number | undefined;

    const handleLogData = (payload: any) => {
      if (!payload || payload.serverId !== serverId) return;

      let newEntries = Array.isArray(payload.entries)
        ? payload.entries.map((entry: WireLogEntry) => parseWireEntry(entry)).filter(Boolean) as LogEntry[]
        : [];
      if (newEntries.length === 0) {
        const rawLines = typeof payload.data === 'string'
          ? payload.data.split('\n')
          : (Array.isArray(payload.data)
            ? payload.data.flatMap((line: unknown) => typeof line === 'string' ? line.split('\n') : [])
            : []);
        newEntries = rawLines
          .filter((line: string) => line.trim() !== '')
          .map((line: string, index: number) => parseLogLine(line, Date.now() + index));
      }
      newEntries = newEntries.filter(entry => {
        if (seenIdsRef.current.has(entry.id)) return false;
        seenIdsRef.current.add(entry.id);
        return true;
      });
      if (newEntries.length === 0) return;

      const latestTimestamp = [...newEntries].reverse().find(entry => entry.timestamp)?.timestamp;
      if (latestTimestamp) followCursorRef.current = latestTimestamp;
      setLogs(prev => [...prev, ...newEntries].slice(-MAX_VISIBLE_LOGS));
      setTotalLogCount(prev => prev + newEntries.length);
    };

    const handleLogReady = (payload: any) => {
      if (payload?.serverId === serverId) setLiveReady(true);
    };

    const handleLogEnded = (payload: any) => {
      if (payload?.serverId !== serverId) return;
      setLiveReady(false);
      retryTimer = window.setTimeout(() => setStreamAttempt(value => value + 1), 1_000);
    };

    const handleLogError = (payload: any) => {
      if (payload?.serverId !== serverId) return;
      setLiveReady(false);
      setError(payload.error || t('dockerLogs.liveFailed'));
      const errorEntry: LogEntry = {
        timestamp: new Date().toISOString(),
        message: `ERROR: ${payload.error}`,
        id: `error-${Date.now()}`,
        stream: 'stderr',
      };
      setLogs(prev => [...prev, errorEntry]);
    };

    socket.on('logs:data', handleLogData);
    socket.on('logs:ready', handleLogReady);
    socket.on('logs:ended', handleLogEnded);
    socket.on('logs:error', handleLogError);
    socket.emit('logs:start', { serverId, since: followCursorRef.current });

    return () => {
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      socket.off('logs:data', handleLogData);
      socket.off('logs:ready', handleLogReady);
      socket.off('logs:ended', handleLogEnded);
      socket.off('logs:error', handleLogError);
      socket.emit('logs:stop', { serverId });
    };
  }, [connected, snapshotLoaded, socket, serverId, streamAttempt, t]);

  const loadOlderLogs = async () => {
    if (isLoadingOlder) return;
    try {
      setIsLoadingOlder(true);
      const requestedLogCount = totalLogCount + 100;
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/logs/${serverId}?tail=${requestedLogCount}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) throw new Error(await getResponseErrorMessage(response, t('dockerLogs.olderFailed')));
      const data = await response.json();
      const structured = parseWireEntries(data.entries);
      const rawLogs = typeof data.logs === 'string' ? data.logs : (Array.isArray(data.logs) ? data.logs.join('\n') : '');
      const allParsedLogs: LogEntry[] = structured.length > 0
        ? structured
        : String(rawLogs).split('\n').filter((line: string) => line.trim() !== '').map(parseLogLine);
      const olderLogs = allParsedLogs.filter(entry => !seenIdsRef.current.has(entry.id));
      olderLogs.forEach(entry => seenIdsRef.current.add(entry.id));
      if (olderLogs.length > 0) setLogs(prev => [...olderLogs, ...prev].slice(-MAX_VISIBLE_LOGS));
      setTotalLogCount(Math.max(totalLogCount, allParsedLogs.length));
    } catch (error) {
      setError(error instanceof Error ? error.message : t('dockerLogs.olderFailed'));
    } finally {
      setIsLoadingOlder(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    setTotalLogCount(0);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isScrolledToBottom = Math.abs(
      element.scrollHeight - element.scrollTop - element.clientHeight
    ) < 5;
    setAutoScroll(isScrolledToBottom);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-5xl h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-dark-600">
          <h2 className="text-xl font-bold text-gray-100">
            {t('dockerLogs.title', { name: serverName })}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadOlderLogs}
              disabled={isLoadingOlder}
              className="btn-secondary text-sm px-3 py-1.5"
            >
              {isLoadingOlder ? (
                <div className="spinner w-3 h-3" />
              ) : (
                <ArrowUp className="w-3.5 h-3.5" />
              )}
              {t('dockerLogs.older')}
            </button>
            <button
              onClick={clearLogs}
              className="btn-secondary text-sm px-3 py-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('dockerLogs.clear')}
            </button>
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                autoScroll
                  ? 'bg-neon-500/20 text-neon-400 border border-neon-500/30'
                  : 'btn-secondary'
              }`}
            >
              {autoScroll ? (
                <ArrowDown className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
              {autoScroll ? t('dockerLogs.autoScroll') : t('dockerLogs.manualScroll')}
            </button>
            <button onClick={onClose} className="btn-ghost p-2" aria-label={t('common.close')}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Log content */}
        <div className="flex-1 overflow-hidden p-4">
          {error && (
            <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-300">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => void loadInitialLogs()} className="btn-secondary text-xs px-2 py-1">
                <RefreshCw className="w-3.5 h-3.5" /> {t('dockerLogs.retry')}
              </button>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <div
              ref={logsContainerRef}
              className="h-full overflow-y-auto bg-dark-950 rounded-lg p-4 font-mono text-sm scrollbar-thin"
              onScroll={handleScroll}
            >
              {logs.length === 0 ? (
                <div className="text-gray-600 text-center py-8">{t('dockerLogs.empty')}</div>
              ) : (
                logs.map((entry) => (
                  <div key={entry.id} className="whitespace-pre-wrap break-words py-0.5 hover:bg-dark-800/50 px-2 -mx-2 rounded">
                    <span className="text-gray-600 text-xs select-none">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                    {' '}
                    <span className={
                      entry.stream === 'stderr' || entry.message.startsWith('ERROR')
                        ? 'text-red-400'
                        : entry.message.includes('WARN')
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }>
                      {entry.message}
                    </span>
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-dark-600 flex items-center justify-between text-xs text-gray-500">
          <span>{t('dockerLogs.lineCount', { count: logs.length })}</span>
          <span className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${liveReady ? 'bg-neon-400 animate-pulse' : 'bg-gray-600'}`} />
            {liveReady ? t('dockerLogs.live') : t('dockerLogs.snapshotOnly')}
          </span>
        </div>
      </div>
    </div>
  );
}
